import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFace } from "@/contexts/FaceContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { getPosition } from "@/lib/geo";
import { bestDistance, FACE_MATCH_THRESHOLD, identifyBest } from "@/lib/face";
import { Chip } from "@/components/Chip";
import { fmtTime } from "@/lib/date";
import { ScanFace, MapPin, CheckCircle2, AlertCircle, Pause, Play, LogIn, LogOut, X, UserCheck } from "lucide-react";
import { toast } from "sonner";

const COOLDOWN_MS = 8000; // don't re-punch the same person within 8s
const CONFIRM_COUNTDOWN_SECONDS = 5; // user sees name + has this many seconds to cancel before auto-record

export default function Kiosk() {
  const { user } = useAuth();
  const { ready: faceReady, status: faceStatus } = useFace();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectRef = useRef(null);
  const lastPunchRef = useRef({}); // userId → ts
  const candidateRef = useRef(null); // { userId, frames } consecutive-frame confirmation
  const [employees, setEmployees] = useState([]);
  const [pos, setPos] = useState(null);
  const [posErr, setPosErr] = useState(null);
  const [scanning, setScanning] = useState(true);
  const [mode, setMode] = useState("in"); // "in" | "out" — explicit kiosk action
  const [feed, setFeed] = useState([]); // recent punches
  const [lastMatch, setLastMatch] = useState(null);
  const [liveHint, setLiveHint] = useState(null);
  const [pendingMatch, setPendingMatch] = useState(null); // { user, distance, secondD } awaiting auto-confirm
  const [countdown, setCountdown] = useState(0);
  const cancelTimerRef = useRef(null);

  const isHR = user?.role === "admin" || user?.role === "hr_manager";

  // Load employees with face descriptors (single OR multi-sample)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setEmployees(data.filter((u) => {
          const fd = u.face_descriptor;
          if (!Array.isArray(fd) || fd.length === 0) return false;
          // legacy single 128-vec OR list of 128-vec
          if (typeof fd[0] === "number") return fd.length === 128;
          return Array.isArray(fd[0]) && fd[0].length === 128;
        }));
      } catch (e) { toast.error("Could not load employees"); }
    })();
  }, []);

  // Acquire GPS once on entry
  useEffect(() => {
    getPosition().then(setPos).catch((e) => setPosErr(e.message || "No location"));
  }, []);

  // Camera
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        toast.error("Camera blocked. Allow camera and reload.");
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (detectRef.current) clearInterval(detectRef.current);
    };
  }, []);

  const punch = useCallback(async (matchedUser) => {
    const last = lastPunchRef.current[matchedUser.user_id] || 0;
    if (Date.now() - last < COOLDOWN_MS) return;
    lastPunchRef.current[matchedUser.user_id] = Date.now();
    try {
      const body = { user_id: matchedUser.user_id, action: mode };
      if (pos) { body.lat = pos.lat; body.lng = pos.lng; body.accuracy = pos.accuracy; }
      const { data } = await api.post("/attendance/kiosk", body);
      const action = mode;
      setLastMatch({ user: matchedUser, action, at: action === "out" ? data.check_out : data.check_in });
      setFeed((f) => [{ id: `${matchedUser.user_id}-${Date.now()}`, user: matchedUser, action, at: action === "out" ? data.check_out : data.check_in }, ...f].slice(0, 10));
      toast.success(`${matchedUser.name} — checked ${action}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      toast.error(`${matchedUser.name}: ${detail}`);
      setFeed((f) => [{ id: `${matchedUser.user_id}-err-${Date.now()}`, user: matchedUser, action: "err", at: new Date().toISOString(), note: detail }, ...f].slice(0, 10));
    }
  }, [pos, mode]);

  // Clear any pending countdown
  const clearCountdown = useCallback(() => {
    if (cancelTimerRef.current) {
      clearInterval(cancelTimerRef.current);
      cancelTimerRef.current = null;
    }
  }, []);

  const cancelPending = useCallback(() => {
    clearCountdown();
    setPendingMatch(null);
    setCountdown(0);
    candidateRef.current = null;
  }, [clearCountdown]);

  const confirmPending = useCallback(() => {
    if (!pendingMatch) return;
    const u = pendingMatch.user;
    clearCountdown();
    setPendingMatch(null);
    setCountdown(0);
    candidateRef.current = null;
    punch(u);
  }, [pendingMatch, punch, clearCountdown]);

  // Countdown ticker for pending confirmation — auto-records when reaches 0
  useEffect(() => {
    if (!pendingMatch) return;
    setCountdown(CONFIRM_COUNTDOWN_SECONDS);
    cancelTimerRef.current = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return clearCountdown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMatch]);

  // When countdown reaches 0, auto-record
  useEffect(() => {
    if (!pendingMatch || countdown !== 0) return;
    clearCountdown();
    const u = pendingMatch.user;
    setPendingMatch(null);
    candidateRef.current = null;
    punch(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, pendingMatch]);

  // Detection loop — 1:N identification with sliding-window voting for robustness
  useEffect(() => {
    if (!faceReady || !scanning || !window.faceapi || !videoRef.current || employees.length === 0) return;
    if (pendingMatch) return; // pause detection while user reviews the match
    const faceapi = window.faceapi;
    // Reset the voting window whenever the loop restarts
    candidateRef.current = { window: [], scans: 0, noFaceStreak: 0 };
    const WINDOW_SIZE = 7;      // last N frames considered
    const MAJORITY = 4;         // need this many votes for same person to confirm
    const loop = async () => {
      try {
        if (videoRef.current.readyState < 2) return;
        const result = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.45 }))
          .withFaceLandmarks()
          .withFaceDescriptor();
        candidateRef.current.scans += 1;
        if (!result) {
          candidateRef.current.noFaceStreak = (candidateRef.current.noFaceStreak || 0) + 1;
          // Hints escalate the longer no face is detected
          if (candidateRef.current.noFaceStreak >= 6) {
            setLiveHint("Move closer & face the camera in good light");
          } else if (candidateRef.current.noFaceStreak >= 3) {
            setLiveHint("Looking for a face…");
          } else {
            setLiveHint(null);
          }
          // Reset voting when we lose the face for too long
          if (candidateRef.current.noFaceStreak >= 3) candidateRef.current.window = [];
          return;
        }
        candidateRef.current.noFaceStreak = 0;
        const live = Array.from(result.descriptor);
        const match = identifyBest(live, employees);
        // Push into sliding window: entry = { id, d } or null if no match
        candidateRef.current.window.push(match.ok ? { id: match.emp.user_id, d: match.d, emp: match.emp } : null);
        if (candidateRef.current.window.length > WINDOW_SIZE) candidateRef.current.window.shift();
        // Tally votes per userId
        const votes = new Map();
        for (const w of candidateRef.current.window) {
          if (!w) continue;
          const cur = votes.get(w.id) || { count: 0, sumD: 0, emp: w.emp };
          cur.count += 1;
          cur.sumD += w.d;
          votes.set(w.id, cur);
        }
        // Find top candidate by count (tie-break by lowest average distance)
        let top = null;
        for (const [id, v] of votes.entries()) {
          const avg = v.sumD / v.count;
          if (!top || v.count > top.count || (v.count === top.count && avg < top.avg)) {
            top = { id, count: v.count, avg, emp: v.emp };
          }
        }
        // Live confidence % based on best distance (used just for UX)
        const conf = match.d < Infinity
          ? Math.max(0, Math.min(100, Math.round((0.7 - match.d) / 0.4 * 100)))
          : 0;
        if (top && top.count >= MAJORITY) {
          const last = lastPunchRef.current[top.id] || 0;
          if (Date.now() - last < COOLDOWN_MS) {
            setLiveHint(`${top.emp.name} just punched — wait…`);
            return;
          }
          setLiveHint(null);
          setPendingMatch({ user: top.emp, d: top.avg, secondD: match.secondD });
          return;
        }
        // Not yet a majority — show progressive feedback
        if (!match.ok) {
          if (match.reason === "ambiguous") {
            setLiveHint(`Hold still — verifying (${conf}%)…`);
          } else if (match.d < 0.6) {
            setLiveHint(`Almost there — hold still (${conf}%)`);
          } else {
            setLiveHint("Face not registered — please register from Team page");
          }
        } else if (top && top.count > 0) {
          setLiveHint(`Confirming ${top.emp.name}… ${top.count}/${MAJORITY} (${conf}%)`);
        } else {
          setLiveHint(`Scanning… (${conf}%)`);
        }
      } catch (_) { /* swallow */ }
    };
    detectRef.current = setInterval(loop, 500);
    return () => detectRef.current && clearInterval(detectRef.current);
  }, [faceReady, scanning, employees, pendingMatch]);

  if (!isHR) {
    return (
      <div className="min-h-[100svh]" style={{ background: "#0A0D14" }}>
        <SubPageHeader title="Kiosk Mode" />
        <div className="p-6 text-sm" style={{ color: "#F75A5A" }}>Only admin / HR can run kiosk mode.</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh]" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="kiosk-page">
      <SubPageHeader title="Kiosk Mode" right={
        <button
          onClick={() => setScanning((s) => !s)}
          data-testid="kiosk-toggle"
          className="h-9 px-3 rounded-lg flex items-center gap-1 text-xs font-bold"
          style={{ background: scanning ? "rgba(247,90,90,0.15)" : "rgba(34,211,165,0.15)", color: scanning ? "#F75A5A" : "#22D3A5", border: `1px solid ${scanning ? "rgba(247,90,90,0.3)" : "rgba(34,211,165,0.3)"}` }}
        >
          {scanning ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
        </button>
      } />

      <div className="px-4 py-3 space-y-3">
        {/* Status row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Chip color={faceReady ? "green" : "amber"} icon={<ScanFace size={11} />}>
            {faceReady ? "AI Ready" : faceStatus}
          </Chip>
          <Chip color={pos ? "green" : "red"} icon={<MapPin size={11} />}>
            {pos ? `±${Math.round(pos.accuracy)}m` : posErr ? "No GPS" : "GPS…"}
          </Chip>
          <Chip color="blue">{employees.length} faces enrolled</Chip>
          <Chip color={scanning ? "green" : "amber"}>{scanning ? "Scanning" : "Paused"}</Chip>
        </div>

        {/* Mode selector: explicit Check-In vs Check-Out */}
        <div className="grid grid-cols-2 gap-2" data-testid="kiosk-mode-toggle">
          <button
            data-testid="kiosk-mode-in"
            onClick={() => { setMode("in"); setLastMatch(null); }}
            className="h-14 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm transition-colors"
            style={mode === "in"
              ? { background: "#22D3A5", color: "#0A0D14", border: "1.5px solid #22D3A5" }
              : { background: "#161B2C", color: "#7A85A3", border: "1.5px solid #1E2640" }}
          >
            <LogIn size={18} /> Check-In
          </button>
          <button
            data-testid="kiosk-mode-out"
            onClick={() => { setMode("out"); setLastMatch(null); }}
            className="h-14 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm transition-colors"
            style={mode === "out"
              ? { background: "#F75A5A", color: "#0A0D14", border: "1.5px solid #F75A5A" }
              : { background: "#161B2C", color: "#7A85A3", border: "1.5px solid #1E2640" }}
          >
            <LogOut size={18} /> Check-Out
          </button>
        </div>

        {/* Camera */}
        <div className="relative rounded-3xl overflow-hidden aspect-[4/5]" style={{ background: "#0A0D14", border: "1px solid #1E2640" }} data-testid="kiosk-camera">
          <video
            ref={videoRef}
            playsInline muted autoPlay
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
          />
          {/* Bracket frame */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative" style={{ width: "70%", aspectRatio: "3/4" }}>
              {[
                "top-0 left-0 border-t-4 border-l-4 rounded-tl-3xl",
                "top-0 right-0 border-t-4 border-r-4 rounded-tr-3xl",
                "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-3xl",
                "bottom-0 right-0 border-b-4 border-r-4 rounded-br-3xl",
              ].map((cls, i) => (
                <div key={i} className={`absolute w-12 h-12 animate-bracket ${cls}`} style={{ borderColor: lastMatch ? "#22D3A5" : mode === "in" ? "#4F8EF7" : "#F7C94F" }} />
              ))}
              {scanning && (
                <div className="absolute inset-x-3 top-0 overflow-hidden h-full">
                  <div
                    className="absolute left-0 right-0 h-1 animate-scanline"
                    style={{
                      background: "linear-gradient(90deg, transparent, #4F8EF7, transparent)",
                      boxShadow: "0 0 12px #4F8EF7",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Live hint pill */}
          {liveHint && !lastMatch && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-[11px] font-mono whitespace-nowrap" style={{ background: "rgba(10,13,20,0.75)", color: "#E8ECFF", border: "1px solid #1E2640" }} data-testid="kiosk-live-hint">
              {liveHint}
            </div>
          )}

          {/* Match toast inside camera */}
          {lastMatch && (
            <div className="absolute bottom-3 left-3 right-3 rounded-2xl p-3 flex items-center gap-3" style={{ background: lastMatch.action === "in" ? "rgba(34,211,165,0.95)" : "rgba(79,142,247,0.95)", color: "#0A0D14" }}>
              <CheckCircle2 size={20} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest">Checked {lastMatch.action}</div>
                <div className="text-base font-bold truncate">{lastMatch.user.name}</div>
              </div>
              <div className="text-xs font-mono">{fmtTime(lastMatch.at)}</div>
            </div>
          )}

          {employees.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center px-6">
              <div className="rounded-2xl p-4 text-center" style={{ background: "rgba(247,201,79,0.15)", border: "1px solid rgba(247,201,79,0.4)", color: "#F7C94F" }}>
                <AlertCircle size={20} className="mx-auto mb-1" />
                <div className="text-sm font-bold">No enrolled faces yet</div>
                <div className="text-xs mt-1" style={{ color: "#E8ECFF" }}>Register employee faces from Team page first.</div>
              </div>
            </div>
          )}

          {/* Confirmation overlay — auto-records after countdown unless cancelled */}
          {pendingMatch && (
            <div className="absolute inset-0 flex items-center justify-center p-4 z-30" style={{ background: "rgba(10,13,20,0.85)", backdropFilter: "blur(6px)" }} data-testid="kiosk-confirm-overlay">
              <div className="w-full max-w-sm rounded-3xl p-5 space-y-3" style={{ background: "#161B2C", border: `2px solid ${mode === "in" ? "#22D3A5" : "#F7C94F"}` }}>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl font-syne" style={{ background: mode === "in" ? "rgba(34,211,165,0.15)" : "rgba(247,201,79,0.15)", color: mode === "in" ? "#22D3A5" : "#F7C94F", border: `2px solid ${mode === "in" ? "#22D3A5" : "#F7C94F"}` }}>
                    {pendingMatch.user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Identified as</div>
                    <div className="font-bold text-lg truncate" style={{ color: "#E8ECFF" }} data-testid="kiosk-confirm-name">{pendingMatch.user.name}</div>
                    <div className="text-[10px] font-mono" style={{ color: "#7A85A3" }}>
                      match=±{pendingMatch.d?.toFixed(2)} · 2nd=±{pendingMatch.secondD === Infinity ? "—" : pendingMatch.secondD?.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="text-center text-sm" style={{ color: "#E8ECFF" }}>
                  Auto-recording <b style={{ color: mode === "in" ? "#22D3A5" : "#F7C94F" }}>Check-{mode === "in" ? "In" : "Out"}</b> in <span className="font-mono text-xl" data-testid="kiosk-countdown">{countdown}</span>s
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={cancelPending}
                    data-testid="kiosk-cancel-confirm"
                    className="h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5"
                    style={{ background: "rgba(247,90,90,0.15)", color: "#F75A5A", border: "1.5px solid #F75A5A" }}
                  >
                    <X size={16} /> Not me
                  </button>
                  <button
                    onClick={confirmPending}
                    data-testid="kiosk-confirm-now"
                    className="h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5"
                    style={{ background: mode === "in" ? "#22D3A5" : "#F7C94F", color: "#0A0D14" }}
                  >
                    <UserCheck size={16} /> Confirm now
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Live feed */}
        <div>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "#7A85A3" }}>Recent Punches</div>
          {feed.length === 0 && <div className="text-xs py-4 text-center" style={{ color: "#7A85A3" }}>Waiting for first face…</div>}
          <div className="space-y-2">
            {feed.map((f) => (
              <div key={f.id} className="rounded-xl p-3 flex items-center gap-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`feed-${f.id}`}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold font-syne" style={{ background: "#1E2640", color: "#E8ECFF" }}>
                  {f.user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate text-sm" style={{ color: "#E8ECFF" }}>{f.user.name}</div>
                  <div className="text-[11px] font-mono" style={{ color: "#7A85A3" }}>{fmtTime(f.at)}{f.note ? ` · ${f.note}` : ""}</div>
                </div>
                <Chip color={f.action === "in" ? "green" : f.action === "out" ? "blue" : "red"}>
                  {f.action === "err" ? "Failed" : `Check ${f.action}`}
                </Chip>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl p-3 text-[11px]" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}>
          Tip: face the camera in good light and hold steady for ~2 seconds. If it doesn&rsquo;t recognise you, move closer or step into brighter light — the scanner keeps trying automatically.
        </div>
      </div>
    </div>
  );
}
